import React, { useState } from "react";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useCategories from "../../../cards/hooks/useCategories";
import { KeyboardArrowDown } from "@mui/icons-material";

const CategoryLinksBar = () => {
  const navigate = useNavigate();
  const { categories, loading } = useCategories();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleCategoryClick = (categoryName: string) => {
    navigate(`/category/${categoryName}`);
  };

  const handleParentClick = (
    event: React.MouseEvent<HTMLElement>,
    categoryId: string,
    hasChildren: boolean
  ) => {
    if (hasChildren) {
      setAnchorEl(event.currentTarget);
      setOpenMenuId(categoryId);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenMenuId(null);
  };

  const handleChildClick = (childName: string) => {
    navigate(`/category/${childName}`);
    handleClose();
  };

  if (
    loading ||
    !categories ||
    !Array.isArray(categories) ||
    categories.length === 0
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        py: 1,
        px: 2,
        backgroundColor: "#047ABC",
        borderBottom: "1px solid #035a8c",
        overflowX: "auto",
        "&::-webkit-scrollbar": {
          height: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(255,255,255,0.1)",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255,255,255,0.3)",
          borderRadius: "2px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(255,255,255,0.5)",
        },
      }}
    >
      {categories.map((category) => {
        const hasChildren = category.children && category.children.length > 0;
        const isOpen = openMenuId === category._id;

        return (
          <React.Fragment key={category._id}>
            <Button
              variant="text"
              onClick={(e) => {
                if (hasChildren) {
                  handleParentClick(e, category._id, true);
                } else {
                  handleCategoryClick(category.name);
                }
              }}
              endIcon={hasChildren ? <KeyboardArrowDown /> : null}
              sx={{
                color: "white",
                fontWeight: 500,
                fontSize: "0.9rem",
                textTransform: "none",
                minWidth: "auto",
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "white",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {category.name}
            </Button>

            {/* Dropdown Menu for Children */}
            {hasChildren && (
              <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    minWidth: 200,
                  },
                }}
              >
                {category.children?.map((child) => (
                  <MenuItem
                    key={child._id}
                    onClick={() => handleChildClick(child.name)}
                    sx={{
                      py: 1.5,
                      px: 3,
                      "&:hover": {
                        backgroundColor: "rgba(4, 122, 188, 0.1)",
                      },
                    }}
                  >
                    {child.name}
                  </MenuItem>
                ))}
              </Menu>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default CategoryLinksBar;
